/**
 * InventoryKpiCard — a single Inventory KPI tile.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §C + §F
 *
 * Mirrors the performance/KpiCard.js structure (label + headline value +
 * optional subtext + optional sparkline).
 *
 * QA2-1 (Lane 241): the per-card status pill ("Monitor" / "Lean" / "Risk" /
 * etc.) was removed -- Ryan asked for the little badges to go. The headline
 * value, subtext and sparkline (the data + metrics) stay; the card no longer
 * consumes the `status` enum. The inventoryStatusColors.js label map is left
 * in place (still unit-tested; simply not rendered here).
 *   - degenerate sparkline handling identical to KpiCard.js
 *     (P2 §D.4 carry: hide sparkline when <2 non-null points; v1 ships
 *     with 1 snapshot so sparklines always hide; §I.2 backfill unblocks)
 *
 * Edge cases:
 *   - Headline value null/undefined -> "—" + muted color
 *   - subtext null -> no subtext row at all
 */
import React from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { formatValue } from '../../utils/formatValue';

const SPARKLINE_HEIGHT = 40;
// Linear --accent / --border, inlined as literals: SVG stroke is a presentation
// ATTRIBUTE where CSS var() does not resolve, so the sparkline carries the token
// VALUES from SupplierDetail.linear.css (--accent:#3a53df, --border:#e9eaee).
const SPARKLINE_STROKE = '#3a53df';
const SPARKLINE_GRID = '#e9eaee';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): the card chrome
// reads the `.sd` Linear token layer so it flips with the shell in the future
// dark lane. The sparkline stroke/tooltip align to the Linear accent + border.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

// formatValue extracted to frontend/src/utils/formatValue.js (Lane 38 Commit 1).
// InventoryKpiCard used higher precision than KpiCard, so it passes its precision
// explicitly at the call site: { pctDigits: 2, decimalDigits: 3 } (+ 'decimal2' → 2).
// This reproduces the prior local behavior exactly (behavior-preserving extraction).
const INVENTORY_FORMAT_OPTS = { pctDigits: 2, decimalDigits: 3 };

/**
 * Sparkline rendered inside an Inventory KPI card. Mirrors the
 * performance/KpiCard.js Sparkline structure verbatim — returns null when
 * fewer than 2 non-null points are available. v1 always returns null
 * (only 1 snapshot exists); §I.2 backfill enables.
 */
function Sparkline({ series }) {
  const cleanCount = (series || [])
    .filter((p) => p && p.value !== null && p.value !== undefined)
    .length;
  if (cleanCount < 2) return null;

  return (
    <div
      data-testid="kpi-sparkline"
      style={{ marginTop: '10px', height: SPARKLINE_HEIGHT }}
    >
      <ResponsiveContainer width="100%" height={SPARKLINE_HEIGHT}>
        <LineChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Tooltip
            cursor={{ stroke: SPARKLINE_GRID, strokeWidth: 1 }}
            contentStyle={{
              fontSize: '11px',
              padding: '6px 8px',
              borderRadius: '6px',
              border: `1px solid ${SPARKLINE_GRID}`,
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}
            labelFormatter={(_, payload) => {
              if (!payload || !payload.length || !payload[0].payload) return '';
              return payload[0].payload.label || '';
            }}
            formatter={(v) => (v === null || v === undefined ? 'N/A' : v)}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={SPARKLINE_STROKE}
            strokeWidth={1.75}
            dot={{ r: 2, fill: SPARKLINE_STROKE, stroke: SPARKLINE_STROKE }}
            activeDot={{ r: 3 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * InventoryKpiCard. Props:
 *   - label        : string — small all-caps card label
 *   - value        : number|string|null — headline value
 *   - format       : 'pct' | 'pct_signed' | 'currency' | 'integer' |
 *                    'decimal' | 'decimal2'
 *   - subtext      : string|node — optional small line below the headline
 *   - series       : [{ label, value }] — optional sparkline data (chronological)
 *
 * The methodology footer (P2 §C.3 L2 amendment) is rendered separately
 * by InventoryKpiCardGrid as a sibling component, NOT inside the card.
 */
function InventoryKpiCard({ label, value, format, subtext, series }) {
  // QA2-1 (Lane 241): the status pill was removed, so the subtext row shows
  // only when there is actual subtext content.
  const showSubtextRow = Boolean(subtext);

  return (
    <div style={{ ...LINEAR_PANEL, padding: '16px 20px' }}>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>

      <div>
        <div
          data-testid="kpi-headline"
          style={{
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            color: value === null || value === undefined
              ? 'var(--text-3)'
              : 'var(--text)',
            lineHeight: 1.1,
          }}
        >
          {formatValue(value, format, INVENTORY_FORMAT_OPTS)}
        </div>
        {showSubtextRow && (
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-3)',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span>{subtext}</span>
          </div>
        )}
      </div>

      <Sparkline series={series || []} />
    </div>
  );
}

export default InventoryKpiCard;
